/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { ID, S3UploadItemMapper } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  FilteredDataQuery,
  getSlicedData,
  identity,
  none,
  optimisticLockingOf,
  privilegedTokenOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { S3UploadItem } from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';

import { defaults, map } from 'lodash';
import { Observable } from 'rxjs';

import { UserAutocompleteDto, UserInboxAutocompleteDto, UserTransferAutocompleteDto } from './user-autocomplete.dto';
import {
  UserAssignedRoleDto,
  UserDetailsDto,
  UserDetailsIntranetDto,
  UserDetailsVerifyDto,
  UserValidateLoginDto,
} from './user-details.dto';
import {
  UserAssignedRoleMapper,
  UserDetailsIntranetMapper,
  UserDetailsMapper,
  UserDetailsVerifyMapper,
  UserSystemRoleMapper,
} from './user-details.mapper';
import {
  OfficeUserListItemDto,
  SalesPersonFilterDto,
  SalesPersonListFilterDto,
  SalesPersonListItemDto,
  UserListFilterDto,
  UserListItemDto,
} from './user-list-item.dto';
import { OfficeUserListItemMapper, UserListFilterMapper, UserListItemMapper } from './user-list-item.mapper';
import { UserSystemRole } from './user-system-role.enum';

export type UserAutocompleteQuery = {
  name: string | undefined;
  showInactive?: boolean;
  excludeIds?: ID[];
  id?: ID;
  autocomplete?: boolean;
  officeRole?: UserSystemRole;
};

export const buildUserFilterByAutocompleteQuery = (query?: UserAutocompleteQuery) => defaults({}, query || {}, {
  name: '',
  showInactive: false,
  autocomplete: true,
});

@Injectable()
export class UserRestService extends AbstractRestService {

  constructor(settings: RestSettings) {
    super(settings);
  }

  checkSystemRole(userId: ID, systemRoles: string): Observable<UserAssignedRoleDto> {
    return this.httpGet(
      `${this.bp}/users/${userId}/validate-role?role=${systemRoles}`,
      UserAssignedRoleMapper,
    );
  }

  create(user: UserDetailsDto): Observable<UserDetailsDto> {
    return this.httpPost(`${this.bp}/users`, user, privilegedTokenOf(optimisticLockingOf(UserDetailsMapper)));
  }

  validateLogin(user: any): Observable<Maybe<UserValidateLoginDto>> {
    return this.httpPost(`${this.bp}/users/validate-login`, user, UserDetailsMapper);
  }

  createAccountForExistingUser(user: UserDetailsDto): Observable<UserDetailsDto> {
    let userData = this.httpPost(`${this.bp}/users/existing-user`, user, UserDetailsMapper)
    return userData;
  }
  
  update(user: UserDetailsDto): Observable<UserDetailsDto> {
    return this.httpPut(`${this.bp}/users/${user.userId}`, user, privilegedTokenOf(optimisticLockingOf(UserDetailsMapper)));
  }

  get(id: ID): Observable<UserDetailsDto> {
    return this.httpGet(`${this.bp}/users/${id}`, optimisticLockingOf(UserDetailsMapper));
  }

  getSystemRoles(): Observable<Hash<boolean>> {
    return this.httpGet(`${this.bp}/users/system-roles`, UserSystemRoleMapper);
  }

  findAllSalesPersons(isForCurrentUser: boolean): Observable<UserListItemDto[]> {
    return this.httpGet(`${this.bp}/users/sales-persons`, { isForCurrentUser }, identity(), arrayOf(UserListItemMapper));
  }

  findAllAsDataSource(filter: UserListFilterDto = {}): SlicedDataSource<UserListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/users`, filter, UserListFilterMapper, arrayOf(UserListItemMapper));
  }

  findAllOfficeAsDataSource(filter: UserListFilterDto = {}): SlicedDataSource<OfficeUserListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/all-users`, filter, UserListFilterMapper, arrayOf(OfficeUserListItemMapper));
  }

  findAllOfficeUsers(filter: UserListFilterDto = {}): Observable<OfficeUserListItemDto[]> {
    return this.httpGet(`${this.bp}/all-users`, filter, UserListFilterMapper, arrayOf(OfficeUserListItemMapper));
  }

  findSalespersonsAsDataSource(filter: SalesPersonListFilterDto = {}): SlicedDataSource<UserListItemDto> {
    return this.createSlicedDataSourcePost(`${this.bp}/users/sales-persons`, filter, identity(), arrayOf(UserListItemMapper));
  }

  getAutocompleteUser(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserAutocompleteDto[]> {
    const userAutocompleteDataAdapter =
      ({ userName, userId }: UserListItemDto) => ({ fullName: userName, userId });
    return this.findAllAsDataSource(buildUserFilterByAutocompleteQuery(query.filter))
      .select(query).pipe(
        map$(getSlicedData),
        map$((users: UserListItemDto[]): UserAutocompleteDto[] => map(users, userAutocompleteDataAdapter)));
  }

  getUserList(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserAutocompleteDto[]> {
    const userAutocompleteDataAdapter = (user: UserListItemDto) => ({
      ...user,
      fullName: user.userName,
    });
    return this.findAllAsDataSource(buildUserFilterByAutocompleteQuery(query.filter))
      .select(query).pipe(
        map$(getSlicedData),
        map$((users: UserListItemDto[]): UserAutocompleteDto[] => map(users, userAutocompleteDataAdapter)));
  }

  getInboxAutocompleteUser(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserInboxAutocompleteDto[]> {
    const userAutocompleteDataAdapter =
      ({ userName, userId }: UserListItemDto) => ({ userName, userId });
    return this.findAllAsDataSource(buildUserFilterByAutocompleteQuery(query.filter))
      .select(query).pipe(
        map$(getSlicedData),
        map$((users: UserListItemDto[]): UserInboxAutocompleteDto[] => map(users, userAutocompleteDataAdapter)));
  }

  getDataToTransferAutocompleteUser(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserTransferAutocompleteDto[]> {
    const userAutocompleteDataAdapter =
      ({ userName, userId }: UserListItemDto) => ({ userName, userId });
    return this.findAllAsDataSource(buildUserFilterByAutocompleteQuery(query.filter))
      .select(query).pipe(
        map$(getSlicedData),
        map$((users: UserListItemDto[]): UserTransferAutocompleteDto[] => map(users, userAutocompleteDataAdapter)));
  }

  getSignature(userId: ID): Observable<S3UploadItem> {
    return this.httpGet(`${this.bp}/users/${userId}/signature`, S3UploadItemMapper);
  }

  getPhoto(userId: ID, size: string = 'ORIGINAL'): Observable<S3UploadItem> {
    return this.httpGet(`${this.bp}/users/${userId}/profile-pic?size=${size}`, S3UploadItemMapper);
  }

  savePhoto(userId: ID, photo: S3UploadItem, coordinates: any, editWindowCoordinates: any): Observable<any> {
    return this.httpPost(
      `${this.bp}/users/${userId}/profile-pic`,
      {
        coordinates,
        editWindowCoordinates,
        name: photo ? photo.name : '',
        key: photo ? photo.key : '',
        location: photo ? photo.location : '',
        mimeType: photo ? photo.mimeType : '',
      },
      identity(),
    );
  }

  createSignature(userId: ID, signature: S3UploadItem): Observable<S3UploadItem> {
    return this.httpPost(
      `${this.bp}/users/${userId}/signature`,
      signature,
      S3UploadItemMapper,
    );
  }

  findAllSalesPersonsAsDataSource(filter: SalesPersonFilterDto): SlicedDataSource<SalesPersonListItemDto> {
    return this.createSlicedDataSourcePost(`${this.bp}/users/sales-persons`, filter, identity(), arrayOf(identity()));
  }

  verifyUser(userData: UserDetailsVerifyDto): Observable<UserDetailsIntranetDto> {
    return this.httpPost(
      `${this.bp}/users/verify`,
      userData,
      UserDetailsVerifyMapper,
      UserDetailsIntranetMapper,
    );
  }

  confirmMaintenance(): Observable<any> {
    return this.httpPut(
      `${this.bp}/users/confirm-maintenance`,
      true,
      identity(),
      none());
  }

  findSupervisors(): Observable<UserListItemDto[]> {
    return this.httpGet(`${this.bp}/users/supervisors`, arrayOf(UserListItemMapper));
  }
}
